import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";

export enum DialogType {
  DocumentAIAnalyzing = "DocumentAIAnalyzing",
}

export type DialogMode = "centered" | "minimizedInTray" | "expandedInTray";

export type MinimizedDialog = {
  id: string;
  dialogType: DialogType;
  props: Record<string, unknown>;
  mode: DialogMode;
};
type CreateDialogProps = Omit<MinimizedDialog, "mode"> & {
  mode?: DialogMode;
};

interface MinimizedDialogsContextType {
  dialogs: MinimizedDialog[];
  /**
   * Create a new dialog.
   * @param props - The properties for the dialog.
   */
  createDialog: (props: CreateDialogProps) => void;
  /**
   * Minimize a dialog.
   * @param id - The ID of the dialog to minimize.
   */
  minimize: (id: string) => void;
  /**
   * Expand a dialog in the tray.
   * @param id - The ID of the dialog to expand.
   */
  expand: (id: string) => void;
  /**
   * Restore a dialog to the center.
   * @param id - The ID of the dialog to restore.
   */
  restore: (id: string) => void;
  /**
   * Close a dialog.
   * @param id - The ID of the dialog to close.
   */
  close: (id: string) => void;
}

const MinimizedDialogsContext = createContext<MinimizedDialogsContextType | undefined>(undefined);

interface MinimizedDialogsProviderProps {
  children: ReactNode;
}

export function MinimizedDialogsProvider({ children }: MinimizedDialogsProviderProps) {
  const [dialogs, setDialogs] = useState<MinimizedDialog[]>([]);

  const createDialog = useCallback((props: CreateDialogProps) => {
    setDialogs((prev) => {
      const filtered = prev.filter((d) => d.id !== props.id);
      return [
        ...filtered,
        {
          ...props,
          mode: props.mode ?? "centered",
        },
      ];
    });
  }, []);

  const minimize = useCallback((id: string) => {
    setDialogs((prev) =>
      prev.map((dialog) => (dialog.id === id ? { ...dialog, mode: "minimizedInTray" } : dialog))
    );
  }, []);

  const expand = useCallback((id: string) => {
    setDialogs((prev) =>
      prev.map((dialog) => (dialog.id === id ? { ...dialog, mode: "expandedInTray" } : dialog))
    );
  }, []);

  const restore = useCallback((id: string) => {
    setDialogs((prev) =>
      prev.map((dialog) => (dialog.id === id ? { ...dialog, mode: "centered" } : dialog))
    );
  }, []);

  const close = useCallback((id: string) => {
    setDialogs((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({
      dialogs,
      createDialog,
      minimize,
      expand,
      restore,
      close,
    }),
    [dialogs, createDialog, minimize, expand, restore, close]
  );

  return (
    <MinimizedDialogsContext.Provider value={contextValue}>
      {children}
    </MinimizedDialogsContext.Provider>
  );
}

/**
 * A hook to give access to the global minimized dialogs context.
 * ex. you can use this to create a new dialog from any component by calling `useMinimizedDialogs().createDialog(props)`.
 * Then, you can use the other methods to minimize, expand, restore, or close the dialog from anywhere else in the app.
 * This is great for long tasks, or dismissable/resumable forms.
 */
export function useMinimizedDialogs(): MinimizedDialogsContextType {
  const context = useContext(MinimizedDialogsContext);
  if (context === undefined) {
    throw new Error("useMinimizedDialogs must be used within a MinimizedDialogsProvider");
  }
  return context;
}
